import React, { useCallback, useEffect, useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { toast } from 'sonner';
import { accountsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const FILTERS = [
  { id: 'attention', label: 'Needs attention', states: 'OUTCOME_UNKNOWN,RESOLVING,PROCESSING,BLOCKED,FAILED' },
  { id: 'all', label: 'All', states: '' },
];
const TONE = {
  COMPLETED: 'text-emerald-400', RESOLVED_APPLIED: 'text-emerald-400', OUTCOME_UNKNOWN: 'text-amber-300',
  PROCESSING: 'text-sky-300', RESOLVING: 'text-amber-300', BLOCKED: 'text-zinc-400', FAILED: 'text-red-400', RESOLVED_NOT_APPLIED: 'text-zinc-400',
};

const ResolveDialog = ({ cmd, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const resolve = async (resolution) => {
    setBusy(true);
    try {
      await accountsAPI.resolvePaymentCommand(cmd.command_id, { resolution, evidence_note: note });
      toast.success(resolution === 'APPLIED' ? 'Marked applied — one ledger credit recorded' : 'Marked not applied');
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Resolution failed');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!cmd} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="resolve-command-dialog">
        <DialogHeader>
          <DialogTitle>Resolve unknown payment outcome</DialogTitle>
          <DialogDescription className="text-xs">
            Check the bank statement / Expense Capture for this bill. "Paid" status alone does not prove this amount was applied.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm">Bill {cmd?.bill_id} · ₹{cmd?.request?.amount_paid} · ref {cmd?.request?.payment_reference || '—'}</p>
        {cmd?.state === 'RESOLVING' && (
          <p className="text-xs text-amber-300" data-testid="resolve-interrupted-note">
            A resolution as {cmd.resolution?.resolution} was started but not finished. Submit the same resolution to complete it; a different one is refused.
          </p>
        )}
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Evidence (e.g. UTR seen on bank statement on 13 Oct)" data-testid="resolve-evidence-note" />
        <div className="flex gap-2 justify-end">
          <Button variant="outline" disabled={busy || note.trim().length < 10} onClick={() => resolve('NOT_APPLIED')} data-testid="resolve-not-applied-btn">Not applied</Button>
          <Button disabled={busy || note.trim().length < 10} onClick={() => resolve('APPLIED')} data-testid="resolve-applied-btn">Applied</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function PaymentCommandsPage() {
  const [filter, setFilter] = useState(FILTERS[0]);
  const [rows, setRows] = useState([]);
  const [resolving, setResolving] = useState(null);
  const perms = usePermissions();
  const begin = useLatestRequest();
  const load = useCallback(async () => {
    const isLatest = begin();
    const res = await accountsAPI.getPaymentCommands(filter.states ? { state: filter.states } : {});
    if (isLatest()) setRows(res.data.commands || []);
  }, [filter, begin]);
  useEffect(() => { load().catch(() => toast.error('Could not load payment commands')); }, [load]);

  return (
    <div className="space-y-6" data-testid="payment-commands-page">
      <div>
        <h1 className="text-3xl font-bold font-manrope">Payment Commands</h1>
        <p className="text-sm text-muted-foreground">Every recorded bill payment attempt. Unknown outcomes are held here and never resent automatically.</p>
      </div>
      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <Button key={f.id} size="sm" variant={f.id === filter.id ? 'default' : 'outline'} onClick={() => setFilter(f)} data-testid={`commands-filter-${f.id}`}>{f.label}</Button>
        ))}
      </div>
      <Card className="bg-card/50 border-border/50">
        <CardHeader><CardTitle className="text-lg">{rows.length} command{rows.length === 1 ? '' : 's'}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Updated</TableHead><TableHead>Bill</TableHead><TableHead className="text-right">Amount</TableHead>
              <TableHead>Reference</TableHead><TableHead>State</TableHead><TableHead>Reason</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => {
                const state = c.state_display || c.state;
                return (
                  <TableRow key={c.command_id} data-testid={`command-row-${c.command_id}`}>
                    <TableCell className="text-xs">{(c.updated_at || '').slice(0, 19).replace('T', ' ')}</TableCell>
                    <TableCell className="font-mono text-xs">{c.bill_id}</TableCell>
                    <TableCell className="text-right font-mono">₹{c.request?.amount_paid ?? '—'}</TableCell>
                    <TableCell className="text-xs">{c.request?.payment_reference || '—'}</TableCell>
                    <TableCell><Badge variant="outline" className={TONE[state]} data-testid={`command-state-${c.command_id}`}>{state}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.reason || ''}</TableCell>
                    <TableCell>
                      {state === 'OUTCOME_UNKNOWN' && perms.financial && (
                        <Button size="sm" variant="outline" onClick={() => setResolving(c)} data-testid={`resolve-btn-${c.command_id}`}>Resolve</Button>
                      )}
                      {state === 'RESOLVING' && perms.financial && (
                        <Button size="sm" variant="outline" onClick={() => setResolving(c)} data-testid={`finish-resolve-btn-${c.command_id}`}>
                          Finish {c.resolution?.resolution === 'APPLIED' ? 'applied' : 'not applied'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <ResolveDialog cmd={resolving} onClose={() => setResolving(null)} onDone={() => { setResolving(null); load(); }} />
    </div>
  );
}
